/** The catalogue rows grid: server-side paging and sort, the standard controls toolbar
 *  (Refresh + auto-refresh, columns, CSV of the loaded rows, free-text search) and the bulk
 *  actions menu over ticked rows or every row matching the filters.
 *
 *  Not preset-wired, on the components.md "embedded grid" ground: it lives inside
 *  ProductSyncForm, which the setup wizard hosts too, and its scope is component state rather
 *  than the page URL, so `?preset=` would be ambiguous. */
import { Box } from '@mui/material';
import {
  DataGridPro,
  type GridPaginationModel,
  type GridRowSelectionModel,
  type GridSlots,
  type GridSortModel,
  type GridToolbarProps,
} from '@mui/x-data-grid-pro';
import type { UseQueryResult } from '@tanstack/react-query';
import { useMemo } from 'react';

import type { CatalogueRow, CatalogueRowPage } from '@/api/catalogue';
import type { BatchActionDef } from '@/components/DataGrid/batchTypes';
import { GridControlsToolbar } from '@/components/DataGrid/GridControlsToolbar';
import { gridRefreshToolbar, useGridAutoRefresh } from '@/components/DataGrid/useGridAutoRefresh';

import type { CatalogueColumnDeps } from './catalogueColumns';
import { buildCatalogueColumns } from './catalogueColumns';

/** The page sizes offered: the rows endpoint caps `page_size` at 500. */
export const CATALOGUE_PAGE_SIZES = [25, 50, 100, 200];

/** Columns whose header sorts server-side (the backend's `ordering` whitelist). */
const SORTABLE: Record<string, string> = { name: 'name', last_synced_ts: 'last_synced_ts' };

export function orderingOf(model: GridSortModel): string | undefined {
  const first = model[0];
  if (!first?.sort || !SORTABLE[first.field]) return undefined;
  return first.sort === 'desc' ? `-${SORTABLE[first.field]}` : SORTABLE[first.field];
}

export interface CatalogueGridProps {
  query: UseQueryResult<CatalogueRowPage>;
  deps: CatalogueColumnDeps;
  pagination: GridPaginationModel;
  onPaginationChange: (m: GridPaginationModel) => void;
  sortModel: GridSortModel;
  onSortModelChange: (m: GridSortModel) => void;
  search: string;
  onSearchChange: (v: string) => void;
  selection: GridRowSelectionModel;
  onSelectionChange: (m: GridRowSelectionModel) => void;
  allMatching: boolean;
  onSelectAllMatching: () => void;
  onClearSelection: () => void;
  batchActions: BatchActionDef[];
  emptyMessage: string;
}

export function CatalogueGrid({
  query,
  deps,
  pagination,
  onPaginationChange,
  sortModel,
  onSortModelChange,
  search,
  onSearchChange,
  selection,
  onSelectionChange,
  allMatching,
  onSelectAllMatching,
  onClearSelection,
  batchActions,
  emptyMessage,
}: CatalogueGridProps) {
  const autoRefresh = useGridAutoRefresh({ grid: 'catalogue-products' });
  const rows = useMemo(() => query.data?.results ?? [], [query.data]);
  const total = query.data?.count ?? 0;
  const columns = useMemo(() => buildCatalogueColumns(deps), [deps]);
  const selectedRows = useMemo(
    () => rows.filter((r) => selection.includes(r.id)),
    [rows, selection],
  );

  return (
    <DataGridPro<CatalogueRow>
      rows={rows}
      rowCount={total}
      columns={columns}
      getRowId={(r) => r.id}
      loading={query.isLoading || query.isFetching}
      getRowHeight={() => 'auto'}
      density="compact"
      // autoHeight disables row virtualization: fine for a small page, never for a big one.
      autoHeight={pagination.pageSize <= 50}
      pagination
      paginationMode="server"
      sortingMode="server"
      paginationModel={pagination}
      onPaginationModelChange={onPaginationChange}
      pageSizeOptions={CATALOGUE_PAGE_SIZES}
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
      checkboxSelection
      rowSelectionModel={selection}
      onRowSelectionModelChange={onSelectionChange}
      keepNonExistentRowsSelected
      disableRowSelectionOnClick
      disableColumnFilter
      getRowClassName={({ row }) => (row.removed_ts ? 'catalogue-row-removed' : '')}
      sx={{
        ...(pagination.pageSize > 50 ? { height: 720 } : {}),
        '& .MuiDataGrid-cell': { alignItems: 'center', display: 'flex', py: 0.5 },
        '& .catalogue-row-removed': { bgcolor: 'action.hover' },
      }}
      slots={{
        toolbar: GridControlsToolbar as GridSlots['toolbar'],
        noRowsOverlay: () => (
          <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>{emptyMessage}</Box>
        ),
      }}
      slotProps={{
        loadingOverlay: { variant: 'linear-progress', noRowsVariant: 'skeleton' },
        toolbar: {
          refresh: gridRefreshToolbar(query, autoRefresh),
          hideFilterButton: true,
          exportFileName: 'catalogue-products',
          search: true,
          searchPlaceholder: 'Name, vendor id, reference, option id…',
          searchValue: search,
          onSearchChange,
          batch: {
            actions: batchActions,
            ids: selection.map((i) => Number(i)),
            allMatching,
            count: allMatching ? total : selection.length,
            matched: total,
            filterQuery: '',
            tenantFilterId: null,
            rowTenantIds: selection.map(() => null),
            rows: selectedRows,
            runStandard: () =>
              Promise.reject(new Error('Catalogue bulk actions use their own endpoints.')),
            clearSelection: onClearSelection,
            onSelectAllMatching,
            isPending: false,
          },
        } as GridToolbarProps,
      }}
    />
  );
}
